/**
 * Previsionnel mensuel, par categorie.
 *
 * Pour un mois donne, chaque categorie se decompose en trois couches dont
 * l'ecran affiche toujours l'origine :
 *
 *   realise   ce qui est deja saisi ;
 *   prevu     les echeances recurrentes pas encore realisees — **certain** ;
 *   estime    pour les depenses variables, ce qui devrait encore venir d'apres
 *             l'historique — **probable**.
 *
 * Chaque valeur existe en deux lectures : `flux` (tresorerie du compte) et
 * `charge` (ma quote-part, pour les budgets).
 *
 * ## Estimer une categorie variable
 *
 * Par defaut, la moyenne des N derniers mois complets. Mais la moyenne ment sur
 * les postes saisonniers : chauffage, vacances, impots. Le mode `auto` mesure
 * donc la dispersion des 12 derniers mois (coefficient de variation) et, au-dela
 * d'un seuil, prend le meme mois de l'an dernier.
 *
 * Les mois anterieurs a la toute premiere operation saisie ne comptent pas
 * comme des zeros : un historique commence le jour ou l'on commence a saisir,
 * sinon la premiere annee d'utilisation sous-estimerait tout.
 */

import {
  ajouterMoisKey,
  dernierJourDuMois,
  formatMois,
  moisDe,
  moisPrecedents,
  premierJourDuMois,
} from '../domain/temps'
import type { Cents, DayKey, Id, ModeEstimation, MoisKey, Reglages, Sens } from '../domain/types'
import { budgetEnVigueur } from './budgets'
import { categorieRacine, chargeReelle, chargeSelonModele, type Index } from './donnees'
import { deriverEcheances } from './recurrences'

export interface Valeur {
  flux: Cents
  charge: Cents
}

const ZERO = (): Valeur => ({ flux: 0, charge: 0 })
const ajouter = (a: Valeur, b: Valeur): Valeur => ({ flux: a.flux + b.flux, charge: a.charge + b.charge })

export type TemporaliteMois = 'passe' | 'courant' | 'futur'

export interface LignePrevisionnel {
  categorieId: Id
  sens: Sens
  realise: Valeur
  prevu: Valeur
  /** Ce qui reste a venir d'apres l'estimation, deja deduit du variable realise. */
  resteEstime: Valeur
  /** Estimation brute du variable pour le mois entier. */
  estimationVariable: Valeur
  projection: Valeur
  source: string
  modeRetenu: ModeEstimation | 'aucun'
  budget: Cents | null
  echeancesEnRetard: number
}

export interface Previsionnel {
  mois: MoisKey
  temporalite: TemporaliteMois
  compteId: Id | null
  lignes: LignePrevisionnel[]
  depenses: { realise: Valeur; projection: Valeur }
  revenus: { realise: Valeur; projection: Valeur }
}

interface Historique {
  /** Montant variable par mois, uniquement pour les mois ou l'on saisissait deja. */
  parMois: Map<MoisKey, Valeur>
  mois: MoisKey[]
}

export function previsionnelDuMois(
  ix: Index,
  mois: MoisKey,
  aujourdhui: DayKey,
  compteId: Id | null,
  reglages: Pick<Reglages, 'moisMoyenne' | 'seuilSaisonnalite' | 'modeEstimation'>,
): Previsionnel {
  const moisCourant = moisDe(aujourdhui)
  const temporalite: TemporaliteMois = mois < moisCourant ? 'passe' : mois > moisCourant ? 'futur' : 'courant'
  const du = premierJourDuMois(mois)
  const au = dernierJourDuMois(mois)
  const dansPerimetre = (cptId: Id) => compteId === null || cptId === compteId

  const lignes = new Map<Id, LignePrevisionnel>()
  const ligne = (categorieId: Id, sens: Sens): LignePrevisionnel => {
    const racine = categorieRacine(ix, categorieId)
    let l = lignes.get(racine)
    if (!l) {
      l = {
        categorieId: racine,
        sens,
        realise: ZERO(),
        prevu: ZERO(),
        resteEstime: ZERO(),
        estimationVariable: ZERO(),
        projection: ZERO(),
        source: '',
        modeRetenu: 'aucun',
        budget: null,
        echeancesEnRetard: 0,
      }
      lignes.set(racine, l)
    }
    return l
  }

  // Realise, et part variable du realise.
  const variableRealise = new Map<Id, Valeur>()
  for (const op of ix.d.operations) {
    if (op.date < du || op.date > au || !dansPerimetre(op.compteId)) continue
    const l = ligne(op.categorieId, op.sens)
    const v = { flux: op.montant, charge: chargeReelle(ix, op) }
    l.realise = ajouter(l.realise, v)
    if (!op.recurrenceId) variableRealise.set(l.categorieId, ajouter(variableRealise.get(l.categorieId) ?? ZERO(), v))
  }

  // Echeances prevues. Pour un mois passe, une echeance jamais realisee est
  // un oubli de saisie, pas une depense a projeter.
  if (temporalite !== 'passe') {
    const echeances = deriverEcheances(
      ix.d.recurrences.filter((r) => dansPerimetre(r.compteId)),
      ix.d.exceptions,
      ix.d.operations,
      du,
      au,
      aujourdhui,
    )
    for (const e of echeances) {
      if (e.statut !== 'prevue') continue
      const l = ligne(e.recurrence.categorieId, e.recurrence.sens)
      const charge =
        e.recurrence.sens === 'depense'
          ? chargeSelonModele(ix, e.montant, e.recurrence.modeleRepartitionId)
          : e.montant
      l.prevu = ajouter(l.prevu, { flux: e.montant, charge })
      if (e.enRetard) l.echeancesEnRetard++
    }
  }

  // Estimation des categories variables.
  const historiques = construireHistoriques(ix, moisCourant, dansPerimetre)
  for (const [racine, h] of historiques) {
    const sens = ix.categorieParId.get(racine)?.sens ?? 'depense'
    const l = ligne(racine, sens)
    const est = estimer(h, mois, reglages)
    l.estimationVariable = est.valeur
    l.source = est.source
    l.modeRetenu = est.mode
    if (temporalite !== 'passe') {
      const deja = variableRealise.get(racine) ?? ZERO()
      l.resteEstime = {
        flux: Math.max(0, est.valeur.flux - deja.flux),
        charge: Math.max(0, est.valeur.charge - deja.charge),
      }
    }
  }

  // Budgets et projection.
  for (const l of lignes.values()) {
    l.projection = ajouter(ajouter(l.realise, l.prevu), l.resteEstime)
    const budgets = ix.d.budgets.filter(
      (b) =>
        b.categorieId === l.categorieId &&
        b.periode === 'mensuel' &&
        budgetEnVigueur(b, mois) &&
        (compteId === null ? b.compteId === null : b.compteId === compteId || b.compteId === null),
    )
    if (budgets.length) l.budget = budgets.reduce((s, b) => s + b.montant, 0)
    if (!l.source) l.source = l.prevu.flux > 0 ? 'échéances récurrentes' : ''
  }

  const toutes = [...lignes.values()]
    .filter((l) => l.projection.flux > 0 || l.budget !== null || l.estimationVariable.flux > 0)
    .sort((a, b) => b.projection.charge - a.projection.charge)

  const total = (sens: Sens, cle: 'realise' | 'projection') =>
    toutes.filter((l) => l.sens === sens).reduce((s, l) => ajouter(s, l[cle]), ZERO())

  return {
    mois,
    temporalite,
    compteId,
    lignes: toutes,
    depenses: { realise: total('depense', 'realise'), projection: total('depense', 'projection') },
    revenus: { realise: total('revenu', 'realise'), projection: total('revenu', 'projection') },
  }
}

/** Montants variables (hors recurrences) par categorie racine et par mois
 *  complet, sur les 12 mois qui precedent le mois courant. */
function construireHistoriques(
  ix: Index,
  moisCourant: MoisKey,
  dansPerimetre: (compteId: Id) => boolean,
): Map<Id, Historique> {
  const fenetre = moisPrecedents(moisCourant, 12)
  const premiereSaisie = ix.d.operations.reduce<DayKey | null>(
    (min, op) => (min === null || op.date < min ? op.date : min),
    null,
  )
  if (premiereSaisie === null) return new Map()
  const moisValides = fenetre.filter((m) => m >= moisDe(premiereSaisie))
  if (!moisValides.length) return new Map()

  const debut = premierJourDuMois(moisValides[0]!)
  const fin = dernierJourDuMois(moisValides[moisValides.length - 1]!)
  const out = new Map<Id, Historique>()

  for (const op of ix.d.operations) {
    if (op.recurrenceId || op.date < debut || op.date > fin || !dansPerimetre(op.compteId)) continue
    const racine = categorieRacine(ix, op.categorieId)
    let h = out.get(racine)
    if (!h) {
      h = { parMois: new Map(moisValides.map((m) => [m, ZERO()])), mois: moisValides }
      out.set(racine, h)
    }
    const m = moisDe(op.date)
    h.parMois.set(m, ajouter(h.parMois.get(m) ?? ZERO(), { flux: op.montant, charge: chargeReelle(ix, op) }))
  }
  return out
}

function estimer(
  h: Historique,
  mois: MoisKey,
  reglages: Pick<Reglages, 'moisMoyenne' | 'seuilSaisonnalite' | 'modeEstimation'>,
): { valeur: Valeur; source: string; mode: ModeEstimation } {
  const n = Math.max(1, Math.min(reglages.moisMoyenne, h.mois.length))
  const recents = h.mois.slice(-n)
  const moyenne: Valeur = {
    flux: Math.round(recents.reduce((s, m) => s + (h.parMois.get(m)?.flux ?? 0), 0) / n),
    charge: Math.round(recents.reduce((s, m) => s + (h.parMois.get(m)?.charge ?? 0), 0) / n),
  }
  const sourceMoyenne = n === 1 ? `d'après ${formatMois(recents[0]!)}` : `moyenne des ${n} derniers mois`

  // Le meme mois calendaire le plus recent dans l'historique.
  const suffixe = mois.slice(4)
  const memeMois = [...h.mois].reverse().find((m) => m.endsWith(suffixe) && m < mois) ?? null

  const versAnneePrecedente = () =>
    memeMois
      ? { valeur: h.parMois.get(memeMois) ?? ZERO(), source: `d'après ${formatMois(memeMois)}`, mode: 'annee-precedente' as const }
      : null

  if (reglages.modeEstimation === 'annee-precedente') {
    return versAnneePrecedente() ?? { valeur: moyenne, source: sourceMoyenne, mode: 'moyenne' }
  }
  if (reglages.modeEstimation === 'auto' && h.mois.length >= 6 && memeMois) {
    const valeurs = h.mois.map((m) => h.parMois.get(m)?.flux ?? 0)
    if (coefficientDeVariation(valeurs) > reglages.seuilSaisonnalite) return versAnneePrecedente()!
  }
  return { valeur: moyenne, source: sourceMoyenne, mode: 'moyenne' }
}

export function coefficientDeVariation(valeurs: readonly number[]): number {
  if (!valeurs.length) return 0
  const moy = valeurs.reduce((s, v) => s + v, 0) / valeurs.length
  if (moy <= 0) return 0
  const variance = valeurs.reduce((s, v) => s + (v - moy) ** 2, 0) / valeurs.length
  return Math.sqrt(variance) / moy
}

/** Mois suivant, exporte pour la navigation de l'ecran. */
export const moisSuivant = (m: MoisKey) => ajouterMoisKey(m, 1)
